/**
 * Kick system — Stage 1c.
 *
 * Pure target selection + Rapier kinematic→dynamic conversion for the viral
 * kick mechanic. Two halves:
 *
 * 1. **Target selection** (`pickKickTarget`) — pure function of the live
 *    threat pool + player pose. Finds the nearest driving threat whose front
 *    face is inside the kick window: still AHEAD of the player's own contact
 *    plane (`contact.playerFrontZ`, the near edge) and no further out than
 *    `max(windowAhead, closingSpeed * windowAheadTime)`, with its centre within
 *    `reachLateral + halfWidth` of the player laterally. Returns the target +
 *    auto-selected leg (right if the vehicle is on/behind the player's right,
 *    else left), or `null` for a whiff. `pickKickTargets` wraps it with the
 *    DOUBLE KICK pair rule (straddled lane boundary + one windowed threat in
 *    each straddled lane + |dz| <= pairMaxDz) — see its own header.
 *
 * 2. **Apply kick** (`applyKick`) — converts the target's Rapier body from
 *    kinematic to dynamic and sets deterministic mass-normalized velocities:
 *      - linvel: -launchBackSpeed along -Z (back where it came from — away from camera),
 *        ±launchLateralSpeed away from the corridor centre / away from the
 *        player (sign = side of vehicle relative to player),
 *        +launchUpCar|launchUpTruck along +Y by kind.
 *      - angvel: a FLAT SPIN-OUT — dominated by YAW about world up (sign derived
 *        from the contact side, magnitude seeded), plus a small tilt SEED about
 *        the friction-true axis `normalize(up × v)` that could not flip the body
 *        on its own. The single TOPPLE onto the side/roof is driven later, on the
 *        ground, by world.ts `enforceWreckGround`. See `computeLaunch`.
 *      - gravityScale from tuning on the body (heavy — the wreck drops fast).
 *      - linear damping = TUNING.kick.wreckAirDamping while airborne, so the
 *        launch sheds speed to air resistance; world.ts restores
 *        wreckLinearDamping (0) on the first ground impact.
 *      - low collider friction + damping, so the landing turns into a long
 *        backward SKID rather than a stop (TUNING.kick.wreckFriction).
 *    The body's collision group switches to THREAT_KICKED (ignores corridor
 *    threats, collides with STATIC only) and NEVER switches back: the exemption
 *    lasts the wreck's whole lifetime, so a wreck sliding down the road for
 *    seconds can never block, deflect or be shoved by live traffic. It may
 *    still smash the roadside (STATIC) and the ground.
 *
 * Determinism: identical (pool snapshot, player pose, rng state) ⇒ identical
 * target + identical launch velocities + spin. Rapier integration of the
 * dynamic body is deterministic given a fixed timestep (world.ts drives it).
 *
 * Collision-group bitfields (InteractionGroups = groups<<16 | mask):
 *   PLAYER          — collides with THREAT_DRIVING + STATIC.
 *   THREAT_DRIVING  — collides with PLAYER + STATIC.
 *   THREAT_KICKED   — collides with STATIC only (ignores PLAYER + other threats).
 *   STATIC          — collides with PLAYER + THREAT_DRIVING + THREAT_KICKED.
 *   CRASH_CYCLIST   — collides with STATIC + CRASH_WALL.
 *   CRASH_WALL      — collides with CRASH_CYCLIST only (invisible to all else).
 * A kicked vehicle therefore never contacts driving threats (the test
 * invariant) but does contact the ground/guardrails.
 */

import type { Rng } from "../core/rng";
import { TUNING } from "./tuning";
import type { PlayerState, VehicleEntity, VehicleKind } from "./types";
import {
  isLive,
  modelLength,
  modelWidth,
  vehicleAbsSpeed,
  type VehiclePool,
} from "./vehicles";

const K = TUNING.kick;
const CT = TUNING.contact;

/**
 * NEAR EDGE of the kick window (m ahead of the player's own front face).
 *
 * A target is only kickable while its front face is still STRICTLY AHEAD of
 * the player's contact plane (`contact.playerFrontZ`). See the pass-through
 * regression note on `pickKickTarget`.
 */
const KICK_NEAR_EDGE_Z = CT.playerFrontZ;

/**
 * Contacts closer than this (front-face metres ahead of the player) launch with
 * NO lateral component — pure -Z + up. See `computeLaunch`.
 */
const NEAR_CONTACT_DIST = 3.0;

// ---------------------------------------------------------------------------
// Collision groups — 16-bit groups (high) | 16-bit mask (low).
// ---------------------------------------------------------------------------
/** Bit positions for each membership group. */
export const GROUP = {
  PLAYER: 1 << 0,
  THREAT_DRIVING: 1 << 1,
  THREAT_KICKED: 1 << 2,
  STATIC: 1 << 3,
  /** The thrown rider capsule spawned on crash (collides with STATIC only). */
  CRASH_CYCLIST: 1 << 4,
  /**
   * Invisible containment walls that keep the thrown rider ON the road.
   *
   * A dedicated membership bit (NOT part of STATIC) whose mask contains only
   * CRASH_CYCLIST, so nothing else in the world can ever notice these walls:
   * the player, driving threats and — deliberately — KICKED vehicles all pass
   * straight through them (a wreck flying off the highway is the fun part; a
   * ragdoll landing in an empty field the camera then frames is not).
   */
  CRASH_WALL: 1 << 5,
} as const;

/** Build an InteractionGroups value: `groupBits` in the high half, `maskBits` in the low half. */
function groups(groupBits: number, maskBits: number): number {
  return ((groupBits & 0xffff) << 16) | (maskBits & 0xffff);
}

/** PLAYER collides with driving threats + static structure. */
export const COLLISION_PLAYER = groups(
  GROUP.PLAYER,
  GROUP.THREAT_DRIVING | GROUP.STATIC,
);
/** Driving threat collides with the player + static structure. */
export const COLLISION_THREAT_DRIVING = groups(
  GROUP.THREAT_DRIVING,
  GROUP.PLAYER | GROUP.STATIC,
);
/** Kicked (airborne) threat collides with STATIC only. */
export const COLLISION_THREAT_KICKED = groups(
  GROUP.THREAT_KICKED,
  GROUP.STATIC,
);
/** Crash cyclist (thrown rider): STATIC structure + the invisible crash walls. */
export const COLLISION_CRASH_CYCLIST = groups(
  GROUP.CRASH_CYCLIST,
  GROUP.STATIC | GROUP.CRASH_WALL,
);
/**
 * Invisible crash-containment wall — collides with the CRASH CYCLIST ONLY.
 *
 * Its membership bit appears in no other body's mask, and its own mask holds
 * nothing but CRASH_CYCLIST, so the walls are invisible to physics for every
 * other body in the world (player, driving threats, kicked wrecks).
 */
export const COLLISION_CRASH_WALL = groups(
  GROUP.CRASH_WALL,
  GROUP.CRASH_CYCLIST,
);
/** Static structure collides with everyone. */
export const COLLISION_STATIC = groups(
  GROUP.STATIC,
  GROUP.PLAYER | GROUP.THREAT_DRIVING | GROUP.THREAT_KICKED | GROUP.CRASH_CYCLIST,
);

// ---------------------------------------------------------------------------
// Target selection
// ---------------------------------------------------------------------------

/** The result of a kick attempt: the chosen target + leg, or a whiff. */
export interface KickTarget {
  vehicle: VehicleEntity;
  leg: "left" | "right";
  /** Front-face z of the vehicle (negative, ahead of player). */
  frontZ: number;
  /** Lateral gap magnitude (|dx| minus half-width) at the moment of kick. */
  lateralGap: number;
  /** Contact point in sim space (front face centre, player-side). */
  contactX: number;
  contactZ: number;
  /**
   * The vehicle's CENTRE OF MASS in sim space — what the leg AIMS at.
   *
   * Distinct from the contact point on purpose (owner playtest 2026-08-02:
   * "the legs aren't aiming at each vehicle's centre of mass — both legs
   * extend forward"). The contact point is pulled half a half-width back
   * toward the player and sits on the vehicle's NEAR FACE, so on a straddle
   * double kick it is barely off the camera axis: for a lane-centred car
   * 1.35 m to the side the contact is only ~0.89 m off, and the leg aimed at
   * it read as "straight ahead". The dust/spark burst still fires at
   * `contactX/contactZ` (that is where the foot physically meets sheet
   * metal); only the leg's thrust DIRECTION uses this.
   */
  aimX: number;
  aimZ: number;
}

/**
 * Pick the nearest kickable driving threat in the kick window.
 *
 * Window (TIME-based, floored in metres): the vehicle's front face must be
 * within `max(windowAhead, closingSpeed * windowAheadTime)` metres ahead:
 * `0 < -frontZ <= window`. A fixed 4 m window was ~0.1 s wide at the closing
 * speeds this game runs at (26–52 m/s) once the crash band (front face at
 * contact.playerFrontZ) is subtracted — unhittable. Scaling with the closing
 * speed keeps the window a constant number of frames at every point on the
 * difficulty ramp. The vehicle centre must additionally be within
 * `reachLateral + halfWidth` laterally of the player (a foot can reach it).
 * Driving only — already-kicked vehicles are skipped.
 *
 * Among candidates, LETHAL ones — vehicles that laterally overlap the player
 * and will crash them if unanswered (the same overlap predicate checkCrash
 * uses: |dx| < halfWidth + contact.playerHalfWidth − forgiveness) — always
 * outrank merely-reachable adjacent-lane ones; within each class the nearest
 * by front-face distance wins, ties broken by smallest lateral gap.
 *
 * WHY THE LETHAL CLASS EXISTS (staggered-wall regression). Kick walls arrive
 * STAGGERED (spawner.ts header 11), so an adjacent lane's wall vehicle is
 * usually a few metres NEARER than the one in the player's own lane. Under
 * pure nearest-first targeting a player answering a wall — settled in the
 * corridor lane, pressing kick at the car bearing down on them — had their
 * kick silently spent on the neighbouring lane's vehicle instead; the 5 s
 * cooldown then made their own lane's vehicle unanswerable. Seen live on
 * seed 42 at t=118: kick connected with the lane-0 truck while the lane-1
 * hatchback killed the player 0.15 s later with 4.9 s of cooldown left.
 *
 * NEAR EDGE — SILENT-PASS-THROUGH REGRESSION. The window used to open at
 * `frontDist > 0`, i.e. anywhere ahead of the CAMERA (z = 0). But the visible
 * bike reaches forward to `contact.playerFrontZ` (-1.45 m) and world.ts runs
 * `tryKick` BEFORE `checkCrash`, while `checkCrash` only ever looks at
 * `motion === "driving"`. So a press landing on the very step a vehicle's front
 * face crossed the contact plane converted an ALREADY-OVERLAPPING vehicle to
 * "kicked" — permanently exempting it from crash detection — and then flung it
 * sideways at `launchLateralSpeed` from inside the handlebars. The player saw
 * the car sweep through the bike with no crash and no penalty ("kick too late,
 * cyclist passes through the vehicle"). Requiring `frontZ <= playerFrontZ`
 * closes the hole: such a press is a whiff and the crash fires the same step,
 * so the outcome is always exactly one of "the vehicle launches" or "the run
 * ends". The near edge and the crash band are adjacent and cannot overlap.
 *
 * @param closingSpeed optional FLOOR on the closing speed used to size the
 *   window. Each candidate's own closing speed (player.forwardSpeed +
 *   vehicleAbsSpeed(v)) is used when it is larger, so callers normally pass
 *   nothing and get a per-vehicle window (a truck closes slower than a car and
 *   gets a correspondingly shorter window in metres — the same window in time).
 * @returns the target + leg, or `null` if no vehicle is in range (whiff).
 */
export function pickKickTarget(
  pool: VehiclePool,
  player: PlayerState,
  closingSpeed = 0,
): KickTarget | null {
  return scanKickTarget(pool, player, closingSpeed, -1, null);
}

/**
 * Shared candidate scan behind `pickKickTarget` and `pickKickTargets`.
 *
 * @param laneFilter  when >= 0, only vehicles whose lane (derived from their x,
 *   see `laneOfX`) equals this index are considered. -1 = every lane, which is
 *   exactly the historical `pickKickTarget` behaviour.
 * @param exclude     a vehicle to skip (the already-chosen primary).
 */
function scanKickTarget(
  pool: VehiclePool,
  player: PlayerState,
  closingSpeed: number,
  laneFilter: number,
  exclude: VehicleEntity | null,
): KickTarget | null {
  const px = player.x;
  const reach = K.reachLateral;
  let best: KickTarget | null = null;
  let bestFrontDist = Infinity;
  let bestLatGap = Infinity;
  let bestLethal = false;

  for (let i = 0; i < pool.maxThreat; i++) {
    const v = pool.slots[i];
    if (!isLive(v) || v.motion !== "driving" || v.role !== "threat") continue;
    if (v === exclude) continue;
    if (laneFilter >= 0 && laneOfX(v.x) !== laneFilter) continue;
    // Front face: the nearest end of the vehicle to the player (z=0).
    // Vehicles approach from -Z toward 0; front face z = v.z + halfLength.
    const halfLen = modelLength(v.model) / 2;
    const frontZ = v.z + halfLen;
    const frontDist = -frontZ; // metres ahead (positive when ahead)
    // NEAR EDGE: the front face must still be ahead of the VISIBLE bike, not
    // merely ahead of the camera — otherwise the kick would cancel a crash that
    // has already begun (see the block comment above).
    if (frontZ > KICK_NEAR_EDGE_Z) continue;
    // Per-vehicle window: constant in TIME, floored in metres.
    const closing = Math.max(closingSpeed, player.forwardSpeed + vehicleAbsSpeed(v));
    const win = Math.max(K.windowAhead, closing * K.windowAheadTime);
    if (frontDist > win) continue;
    const halfWidth = modelWidth(v.model) / 2;
    const dx = v.x - px;
    const latGap = Math.abs(dx) - halfWidth;
    if (latGap > reach) continue; // too far laterally for a foot
    // LETHAL: this vehicle laterally overlaps the player and will crash them
    // if unanswered — the same overlap predicate checkCrash uses. Lethal
    // candidates always outrank adjacent-lane ones (see block comment above).
    const lethal =
      Math.abs(dx) <
      halfWidth + TUNING.contact.playerHalfWidth - TUNING.contact.forgiveness;
    // Class first (lethal > reachable), then nearest by front distance,
    // tie-break by smallest lateral gap.
    if (
      (lethal && !bestLethal) ||
      (lethal === bestLethal &&
        (frontDist < bestFrontDist ||
          (frontDist === bestFrontDist && latGap < bestLatGap)))
    ) {
      bestLethal = lethal;
      bestFrontDist = frontDist;
      bestLatGap = latGap;
      const leg: "left" | "right" = v.x >= px ? "right" : "left";
      // Contact point: front face centre, on the player-facing side of the
      // vehicle body (clamped to the vehicle's near face).
      const contactX = v.x - Math.sign(dx) * halfWidth * 0.5;
      best = {
        vehicle: v,
        leg,
        frontZ,
        lateralGap: latGap,
        contactX,
        contactZ: frontZ,
        // Centre of mass = the body centre the render leg thrusts toward.
        aimX: v.x,
        aimZ: v.z,
      };
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// DOUBLE KICK — pair selection
// ---------------------------------------------------------------------------

/**
 * Lane index (0..laneCount-1) whose centre `x` is nearest, or -1 when `x` lies
 * further than half a lane outside the outermost centre (shoulder/rail).
 */
function laneOfX(x: number): number {
  const w = TUNING.road.laneWidth;
  const lane = Math.round(x / w) + 1;
  if (lane < 0 || lane > TUNING.road.laneCount - 1) return -1;
  if (Math.abs(x - (lane - 1) * w) > w / 2 + 1e-6) return -1;
  return lane;
}

/** The result of a kick attempt: one target, or a DOUBLE-KICK pair. */
export interface KickTargets {
  primary: KickTarget;
  /** The other straddled lane's target on a double kick; null otherwise. */
  secondary: KickTarget | null;
}

/**
 * Pick the kick target(s) — the DOUBLE KICK entry point used by world.ts.
 *
 * `primary` is exactly what `pickKickTarget` returns (unchanged lethal-class
 * priority; see its header). `secondary` is non-null ONLY when all three of the
 * double-kick conditions hold:
 *
 *  1. STRADDLE. The player's distance to the NEAREST lane centre is at least
 *     `TUNING.kick.straddleMin` (0.8 m of the 1.35 m half-lane), i.e. the rider
 *     is committed to a lane BOUNDARY with a foot's worth of body over each
 *     side. A lane-centred player therefore never doubles by accident, which is
 *     what keeps one cooldown = one vehicle the default case.
 *  2. ONE THREAT IN EACH STRADDLED LANE, each inside its OWN kick window (the
 *     same per-vehicle window/reach/near-edge tests the single kick uses — the
 *     scan is literally the same function, restricted to a lane). The primary
 *     must itself be in one of the two straddled lanes.
 *  3. PAIRED IN Z. `|z_primary - z_secondary| <= TUNING.kick.pairMaxDz` — the
 *     two vehicles are ALONGSIDE each other, not staggered down the road.
 *
 * The secondary's leg is forced OPPOSITE the primary's: the pair straddles the
 * player, so one leg each is the physical read, and the render drives the two
 * leg instances off exactly this field.
 */
export function pickKickTargets(
  pool: VehiclePool,
  player: PlayerState,
  closingSpeed = 0,
): KickTargets | null {
  const primary = pickKickTarget(pool, player, closingSpeed);
  if (!primary) return null;

  const w = TUNING.road.laneWidth;
  const px = player.x;
  // (1) STRADDLE: far enough from every lane centre.
  const nearestLane = Math.max(
    0,
    Math.min(TUNING.road.laneCount - 1, Math.round(px / w) + 1),
  );
  if (Math.abs(px - (nearestLane - 1) * w) < K.straddleMin) {
    return { primary, secondary: null };
  }
  // The two lanes the player straddles (lane coordinate px/w + 1 lies between
  // them). Both must exist — a player pushed onto the shoulder straddles only
  // one real lane.
  const loLane = Math.floor(px / w + 1);
  const hiLane = loLane + 1;
  if (loLane < 0 || hiLane > TUNING.road.laneCount - 1) {
    return { primary, secondary: null };
  }
  // (2) The primary must be in one of them; the secondary is the OTHER one's
  // best candidate under the same window/reach rules.
  const primaryLane = laneOfX(primary.vehicle.x);
  let otherLane: number;
  if (primaryLane === loLane) otherLane = hiLane;
  else if (primaryLane === hiLane) otherLane = loLane;
  else return { primary, secondary: null };

  const secondary = scanKickTarget(
    pool,
    player,
    closingSpeed,
    otherLane,
    primary.vehicle,
  );
  if (!secondary) return { primary, secondary: null };
  // (3) PAIRED IN Z.
  if (Math.abs(primary.vehicle.z - secondary.vehicle.z) > K.pairMaxDz) {
    return { primary, secondary: null };
  }
  secondary.leg = primary.leg === "left" ? "right" : "left";
  return { primary, secondary };
}

// ---------------------------------------------------------------------------
// Launch velocity computation (pure, for testability)
// ---------------------------------------------------------------------------

/**
 * THE FRICTION-TRUE TUMBLE AXIS — `normalize(up × v)` for a horizontal velocity
 * `(vx, _, vz)`, in the world frame (up = +Y).
 *
 * `up × v = (vz, 0, -vx)`. POSITIVE angular velocity about this unit axis rolls
 * the body's TOP in the direction of travel, which is what road friction —
 * acting at ground level, a lever below the centre of mass, opposing v — does
 * to a sliding car: it trips over its own leading edge.
 *
 * Shared by the launch (`computeLaunch`, from the launch velocity) and by the
 * ground controller (world.ts `enforceWreckGround`, from
 * the wreck's LIVE velocity each step) so both spin the same way by
 * construction. Pure, allocation-light, no rng: determinism is untouched.
 *
 * Degenerate input (a wreck with no horizontal motion) returns the canonical
 * backward-launch axis (-1, 0, 0), i.e. the same end-over-end pitch a wreck
 * travelling down -Z gets — never a zero vector.
 */
export function frictionTumbleAxis(vx: number, vz: number): { x: number; z: number } {
  const len = Math.hypot(vz, vx);
  if (len < 1e-6) return { x: -1, z: 0 };
  return { x: vz / len, z: -vx / len };
}

/**
 * TOPPLE MODE — how this wreck will go over on the ground, decided at launch
 * (world.ts `enforceWreckGround` consumes it).
 *
 *  - `"sideRoll"` — the body rolls about its own LONG axis and lands on its
 *    flank. The only way a high-CG body (box truck, semi) can go over, and the
 *    commonest real outcome for a car that trips over its wheels while sliding
 *    sideways.
 *  - `"trip"` — the body goes over the friction-true axis `normalize(up × v)`,
 *    i.e. it trips over its leading edge and can end on its roof. Cars only, on
 *    a 50/50 seeded coin.
 */
export type ToppleMode = "trip" | "sideRoll";

/** Computed deterministic launch velocities for a kicked vehicle. */
export interface KickLaunch {
  /** Linear velocity (m/s) in sim space [x, y, z]. */
  linvel: { x: number; y: number; z: number };
  /** Angular velocity (rad/s) [x, y, z]. */
  angvel: { x: number; y: number; z: number };
  /** How this wreck will topple on the ground (see `ToppleMode`). */
  topple: ToppleMode;
  /** Sign (+1/-1) of the spin-out yaw, so the ground controller can hold it. */
  yawSign: number;
}

/**
 * Compute the mass-normalized launch velocities for a kick — a GROUND-TIGHT,
 * FLAT SPIN-OUT (see TUNING.kick, "LAUNCH SPIN: A FLAT SPIN-OUT, NOT A TUMBLE").
 *
 * - Back: -launchBackSpeed along -Z (the vehicle came from -Z ahead of the
 *   player; -Z sends it back where it came from — away from the camera), floored
 *   at `launchBackClosingRatio` of the live closing speed. This carries the
 *   "impossibly strong kick": the wreck LEAVES, hard.
 * - Lateral: ±launchLateralSpeed away from the player. Sign = side of vehicle
 *   relative to player: vehicle on player's right (dx>0) → pushed further right
 *   (+x). Suppressed entirely at point-blank range (see below).
 * - Up: +launchUpCar | +launchUpTruck — DELIBERATELY NEAR ZERO. Just enough to
 *   unweight the body (apex ~1.7 cm for a car at gravityScale 5.8); the wreck
 *   hops, it never flies. This is the single constant that separates the current
 *   model from the three rejected acrobatic ones.
 * - Spin: dominated by YAW about world up — a flat spin-out — plus a small tilt
 *   SEED about the friction-true axis `normalize(up × v)` that could never flip
 *   the vehicle on its own (the topple is driven on the ground, by grip).
 *
 * THE YAW SIGN IS DERIVED, NOT DRAWN. The boot lands on the vehicle's
 * player-facing flank, half a half-width off centre (see `scanKickTarget`'s
 * `contactX`), so the impulse is off-centre: with r ≈ -sign(dx)·(w/4)·x̂ and
 * F ≈ -F·ẑ, τ = r × F has τ_y = -sign(dx)·(w/4)·F. A vehicle on the player's
 * RIGHT therefore takes NEGATIVE yaw (its far flank swings back), and one on the
 * left takes positive. Deterministic, and it always spins away from the contact
 * side, which is what a viewer reads as "the kick caught its corner".
 *
 * POINT-BLANK CONTACTS GET NO LATERAL. Below `NEAR_CONTACT_DIST` metres of
 * front-face distance the lateral component is dropped entirely (pure -Z + up).
 * At full lateral speed a vehicle kicked from ~1.5 m sweeps its body across the
 * camera before the -Z velocity has carried it clear, which reads as the wreck
 * passing THROUGH the rider.
 *
 * @param kind      vehicle kind (car vs truck → up speed, spin scale, topple).
 * @param dx        vehicle.x - player.x (sign selects lateral + yaw direction).
 * @param rng       seeded RNG (yaw magnitude, jitter, car topple coin).
 * @param frontDist metres from the player to the target's front face. Defaults
 *   to Infinity (no near-contact suppression) for callers that only care about
 *   the direction signs.
 */
export function computeLaunch(
  kind: VehicleKind,
  dx: number,
  rng: Rng,
  closingSpeed = 0,
  frontDist = Infinity,
): KickLaunch {
  const sideSign = dx >= 0 ? 1 : -1;
  const up = kind === "truck" ? K.launchUpTruck : K.launchUpCar;
  const lateralSpeed = frontDist < NEAR_CONTACT_DIST ? 0 : K.launchLateralSpeed;

  // Back speed scales with how fast the vehicle was actually closing, floored
  // at launchBackSpeed. A fixed absolute speed stops reading as a launch once
  // the director has ramped closing speed past it — the car then leaves slower
  // than it arrived. See TUNING.kick.launchBackClosingRatio.
  const backSpeed = Math.max(
    K.launchBackSpeed,
    closingSpeed * K.launchBackClosingRatio,
  );

  const linvel = {
    x: sideSign * lateralSpeed,
    y: up,
    z: -backSpeed,
  };

  // SIZE SCALE — a truck spins more slowly than a car for the same impulse (the
  // moment of inertia grows faster than the lever). See tumbleTruckScale; the
  // ground controller scales by the model's actual length.
  const sizeScale = kind === "truck" ? K.tumbleTruckScale : 1;
  const jitter = 1 + (rng() - 0.5) * 2 * K.tumbleJitter;
  const yawMag = K.launchYawMin + rng() * (K.launchYawMax - K.launchYawMin);
  // TOPPLE MODE. Trucks and semis ALWAYS roll sideways — an 11 m body with a
  // high centre of mass cannot credibly go end-over-end — and cars take a 50/50
  // seeded coin. The draw happens unconditionally so the rng sequence does not
  // depend on the kind.
  const coin = rng();
  const topple: ToppleMode =
    kind === "truck" ? "sideRoll" : coin < 0.5 ? "trip" : "sideRoll";

  // YAW: derived sign (see the header), seeded magnitude.
  const yawSign = -sideSign;
  // TILT SEED about the friction-true axis: positive ω about normalize(up × v)
  // rolls the body's top in the direction of travel, which is what road friction
  // does to a sliding body. Far too small to flip anything — the topple is the
  // ground controller's job.
  const axis = frictionTumbleAxis(linvel.x, linvel.z);
  const tilt = K.launchTiltRate * sizeScale;
  const angvel = {
    x: axis.x * tilt,
    y: yawSign * yawMag * jitter * sizeScale,
    z: axis.z * tilt,
  };

  return { linvel, angvel, topple, yawSign };
}

// ---------------------------------------------------------------------------
// Rapier body handle — world.ts maps each threat slot to a RigidBody and
// passes a lookup to applyKick. This keeps Rapier out of the pure selection
// path while letting the conversion live here.
// ---------------------------------------------------------------------------

/** The subset of a Rapier collider `applyKick` touches. */
export interface KickCollider {
  setCollisionGroups(groups: number): void;
  /** Ground friction — set to K.wreckFriction so the wreck SKIDS on landing. */
  setFriction(friction: number): void;
}

/** Minimal Rapier body surface applyKick needs (structural typing, no import of the class). */
export interface KickRigidBody {
  setBodyType(type: number, wakeUp: boolean): void;
  setLinvel(vel: { x: number; y: number; z: number }, wakeUp: boolean): void;
  setAngvel(vel: { x: number; y: number; z: number }, wakeUp: boolean): void;
  setGravityScale(factor: number, wakeUp: boolean): void;
  setLinearDamping(damping: number): void;
  setAngularDamping(damping: number): void;
  setEnabled(enabled: boolean): void;
  collider(index: number): KickCollider;
  numColliders(): number;
}

/** A handle map: threat slot id → Rapier body (or null if the slot has no body). */
export type BodyMap = (KickRigidBody | null)[];

/**
 * Apply a kick to a target: convert its body kinematic→dynamic, set the
 * deterministic launch velocities + spin, switch collision group to
 * THREAT_KICKED, and return the launch that was applied (for events/tests).
 *
 * Mutates the VehicleEntity (`motion` → "kicked", `kickedAt`) in place. The
 * caller (world.ts) is responsible for syncing the body's translation back
 * into the entity each step while it is kicked.
 *
 * @returns the launch velocities applied, or `null` if the body is missing
 *   (defensive — should not happen for a live driving threat).
 */
export function applyKick(
  target: KickTarget,
  time: number,
  dx: number,
  rng: Rng,
  bodies: BodyMap,
  closingSpeed = 0,
): KickLaunch | null {
  const v = target.vehicle;
  const body = bodies[v.id];
  if (!body) return null;

  // The target's own front-face distance drives the point-blank lateral
  // suppression in computeLaunch.
  const launch = computeLaunch(v.kind, dx, rng, closingSpeed, -target.frontZ);

  // Convert kinematic → dynamic and arm it.
  body.setBodyType(0 /* RAPIER.RigidBodyType.Dynamic */, true);
  body.setEnabled(true);
  body.setLinvel(launch.linvel, true);
  body.setAngvel(launch.angvel, true);
  body.setGravityScale(K.gravityScale, true);
  // Damping: AIR DRAG while airborne (K.wreckAirDamping) so the launch visibly
  // decelerates — the "heavy object fighting the air" read. world.ts swaps it
  // back to K.wreckLinearDamping (0) on the first ground impact, because on the
  // ground the speed loss must come from FRICTION alone or the long approved
  // skid is damped away before it reads.
  body.setLinearDamping(K.wreckAirDamping);
  body.setAngularDamping(K.wreckAngularDamping);

  // Switch collision group: kicked bodies ignore corridor traffic (STATIC only)
  // for the REST OF THEIR LIFETIME — nothing ever restores the driving groups.
  // Low friction turns the landing into a long backward skid; see
  // TUNING.kick.wreckFriction.
  const n = body.numColliders();
  for (let i = 0; i < n; i++) {
    const c = body.collider(i);
    c.setCollisionGroups(COLLISION_THREAT_KICKED);
    c.setFriction(K.wreckFriction);
  }

  v.motion = "kicked";
  v.kickedAt = time;
  return launch;
}
